import { CloseOutlineIcon, RightOutlineIcon } from '@carboncopyco/icons';
import type { EditToolColorValues } from '@carboncopyco/types';
import {
  ColorBadge,
  ColorPicker,
  Flex,
  InputText,
  SelectButton,
  Spacer,
  TextBoxEditorTitle,
  TextCaption,
  Tooltip,
} from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import type React from 'react';
import { useRef } from 'react';
import rgbHex from 'rgb-hex';
import { EditToolBox } from '../EditToolBox';

export interface EditToolBackgroundColorPickerProps {
  title?: string;
  onChange: (values: EditToolColorValues) => void;
  values: EditToolColorValues;
  appendTo?: string | HTMLElement | (() => HTMLElement);
  isClickOutsideDisabled?: boolean;
  isOpacityInputFieldVisible?: boolean;
  disabled?: boolean;
}

export const EditToolBackgroundColorPicker = ({
  title,
  onChange,
  values,
  appendTo,
  disabled,
  isClickOutsideDisabled = false,
  isOpacityInputFieldVisible = true,
}: EditToolBackgroundColorPickerProps) => {
  const [openColorPicker, setOpenColorPicker] = useBoolean();
  const opacityInputRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<HTMLButtonElement>(null);

  const handleOnChangeColor = (color: any) => {
    onChange({
      ...values,
      r: color.rgb.r,
      g: color.rgb.g,
      b: color.rgb.b,
      a: color.rgb.a,
    });
  };

  const handleOnChangePercent = (event: any) => {
    const value = event.target.value;
    if (Number.isNaN(value)) {
      return;
    }

    let alpha = Number(value.replace('%', ''));
    if (alpha >= 100) {
      alpha = 100;
    } else if (alpha < 0) {
      alpha = 0;
    }

    onChange({
      ...values,
      a: alpha / 100,
    });
  };

  return (
    <>
      <EditToolBox>
        <Flex alignItems="center">
          <Flex col>
            {title && (
              <TextCaption
                label={title}
                color="text-ds-natural-300"
              />
            )}
            <Spacer amount={0.5} />
            <SelectButton
              disabled={disabled}
              ref={pickerRef as React.RefObject<HTMLButtonElement>}
              isActive={openColorPicker}
              label={`#${rgbHex(values.r, values.g, values.b)}`}
              rightIcon={<RightOutlineIcon />}
              onClick={() => setOpenColorPicker.toggle()}
            >
              <ColorBadge
                color={`#${rgbHex(values.r, values.g, values.b, values.a)}`}
              />
              <Spacer amount={0.75} />
            </SelectButton>
          </Flex>
          {isOpacityInputFieldVisible && (
            <>
              <Spacer amount={0.5} />

              <div className="w-ds-70 flex flex-col">
                {title && (
                  <TextCaption
                    label="Opacity (%)"
                    color="text-ds-natural-300"
                  />
                )}
                <Spacer amount={0.5} />
                <InputText
                  ref={opacityInputRef}
                  name="opacityPercent"
                  onChange={handleOnChangePercent}
                  value={`${Math.trunc(values.a * 100)}`}
                />
              </div>
            </>
          )}
        </Flex>
      </EditToolBox>
      {openColorPicker && (
        <Tooltip
          appendTo={appendTo}
          visible={openColorPicker}
          noArrow
          placement="bottom-start"
          anchorRef={pickerRef}
          onClickOutside={() => {
            if (isClickOutsideDisabled) {
              return;
            }
            setOpenColorPicker.setFalse();
          }}
        >
          <div className="flex flex-col bg-ds-accent3-100 p-4 rounded-md shadow-ds-edit-tool">
            <div className="flex justify-between">
              <TextBoxEditorTitle
                label="Color Picker"
                color="text-ds-natural-600"
                uppercase
                fontWeight="font-ds-medium"
              />
              <div
                className="text-ds-natural-300 cursor-pointer"
                onClick={setOpenColorPicker.setFalse}
              >
                <CloseOutlineIcon size={20} />
              </div>
            </div>
            <Spacer amount={1.5} />
            <ColorPicker
              onChange={handleOnChangeColor}
              color={`#${rgbHex(values.r, values.g, values.b)}`}
              alpha={Number(values.a)}
            />
          </div>
        </Tooltip>
      )}
    </>
  );
};
